import styled from "@emotion/styled";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faDiscord,
  faLinkedin,
  faTelegramPlane,
  faTwitter,
  IconLookup,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../redux/root";
import {
  earnReferralSocialBonus as earnReferralSocialBonusAction,
  ReferralSocialMedium,
} from "../../redux/team";
import { analyticsEvent } from "@util/analytics";
import { getReferralUrlForApp } from "@util/appUrls.ts";

import BaseButton from "../revamped/button/BaseButton";

const Button = styled(BaseButton)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 150%;
  font-weight: 500;
  background: #000;
  color: white;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background: #254cdd;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
`;

const Badge = styled.div`
  position: absolute;
  top: -4px;
  right: -10px;
  padding: 1px 4px;
  border-radius: 100px;
  background: linear-gradient(118.5deg, #44eda6 15.79%, #1eb7e8 124.44%);
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
  color: #000;
`;

type SocialMedium = Exclude<ReferralSocialMedium, "email">;

const networkLabels: Record<SocialMedium, string> = {
  twitter: "Twitter",
  linkedin: "LinkedIn",
  discord: "Discord",
  telegram: "Telegram",
};

const networkIcons: Record<SocialMedium, IconLookup> = {
  twitter: faTwitter,
  linkedin: faLinkedin,
  discord: faDiscord,
  telegram: faTelegramPlane,
};

const socialSharingCopy = `Web3 devs! Crush it with me on Alchemy - the ultimate platform for web3 development. Use my link to get $100 credits on the growth tier and start building your dapp. Join now: {URL}\n#poweredByAlchemy`;

const networkShareLinks: Record<SocialMedium, string> = {
  twitter: "https://twitter.com/intent/tweet?text={TEXT}",
  linkedin: "https://www.linkedin.com/shareArticle?url={URL}",
  discord: "https://discord.com/app",
  telegram: "https://t.me/share/url?url={URL}&text={TEXT}",
};

interface ReferralSocialButtonProps {
  medium: SocialMedium;
}

const ReferralSocialButton = ({ medium }: ReferralSocialButtonProps) => {
  const dispatch = useDispatch();
  const referralSocialBonuses = useSelector(
    (state: RootState) => state.team.referralSocialBonuses,
  );
  const earningSocialBonus = useSelector(
    (state: RootState) => state.team.earningSocialBonus,
  );

  const receivedBonusForMedium =
    referralSocialBonuses.value?.mediums.includes(medium);
  const referralUrl = getReferralUrlForApp();

  const handleClick = useCallback(() => {
    const socialText = socialSharingCopy.replace(/{URL}/, referralUrl);

    window.open(
      networkShareLinks[medium]
        .replace(/{TEXT}/g, encodeURIComponent(socialText))
        .replace(/{URL}/g, encodeURIComponent(referralUrl)),
    );

    analyticsEvent("Referral: Clicked on social share button", { medium });

    if (!receivedBonusForMedium) {
      dispatch(earnReferralSocialBonusAction({ medium }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [medium, receivedBonusForMedium, referralUrl]);

  return (
    <Button onClick={handleClick} disabled={earningSocialBonus.isLoading}>
      <IconContainer>
        <FontAwesomeIcon icon={networkIcons[medium] as IconProp} size="lg" />
      </IconContainer>
      Share on {networkLabels[medium]}
      {receivedBonusForMedium ? null : <Badge>$10</Badge>}
    </Button>
  );
};

export default ReferralSocialButton;
